import { createFileRoute, Link } from "@tanstack/react-router";
import { ShieldCheck, CalendarCheck, Rocket, BedDouble, Headphones, MapPin, Star, Hotel, Palmtree, Building2, PartyPopper, Trees, Waves, Building, LayoutGrid, Presentation, Flame, WifiOff, RefreshCcw } from "lucide-react";
import heroImg from "@/assets/hero.jpg";
import { SearchBar } from "@/components/venues/SearchBar";
import { VenueCard } from "@/components/venues/VenueCard";
import { Skeleton } from "@/components/ui/skeleton";
import { BROWSABLE_CATEGORIES, compareByGuestRating, type Venue } from "@/data/venues";
import { VenueService } from "@/services/venue.service";
import { Button } from "@/components/ui/button";
import { useState, useEffect, useMemo, useRef } from "react";

const ICON_MAP: Record<string, React.ElementType> = {
  LayoutGrid, Hotel, Palmtree, Building2, PartyPopper, Trees, Waves, Building, Presentation,
};

const homeSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": "https://oyobooking.ng/#website",
      "url": "https://oyobooking.ng/",
      "name": "OyoBooking",
      "description": "Premium Hotels, Suites, Event Centres and Gardens in Oyo State.",
      "publisher": {
        "@id": "https://oyobooking.ng/#organization"
      }
    },
    {
      "@type": "Organization",
      "@id": "https://oyobooking.ng/#organization",
      "name": "OyoBooking",
      "url": "https://oyobooking.ng/",
      "logo": "https://oyobooking.ng/src/assets/favicon.webp",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+234-800-OYO-BOOK",
        "contactType": "customer service",
        "areaServed": "NG"
      }
    }
  ]
};

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "OyoBooking — Premium Hotels & Event Venues in Oyo State" },
      {
        name: "description",
        content:
          "Discover, compare and book the finest hotels, event centres and open spaces across Oyo State, Nigeria.",
      },
      { "script:ld+json": homeSchema },
    ],
    links: [{ rel: "canonical", href: "https://oyobooking.ng/" }],
  }),
  component: HomePage,
});

function VenueCardSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="aspect-[4/3] w-full rounded-xl" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-3 w-1/2" />
      <Skeleton className="h-3 w-5/6" />
      <div className="flex gap-1 pt-0.5">
        <Skeleton className="h-4 w-12 rounded" />
        <Skeleton className="h-4 w-12 rounded" />
        <Skeleton className="h-4 w-12 rounded" />
      </div>
    </div>
  );
}

function getFilteredVenues(venues: Venue[], activeCategory: string) {
  if (activeCategory === "all") {
    return [...venues].sort(compareByGuestRating).slice(0, 8);
  }

  return venues
    .filter((v) => {
      const cat = BROWSABLE_CATEGORIES.find((c) => c.id === activeCategory);
      if (!cat) return true;
      if (cat.category && v.category !== cat.category) return false;
      if (cat.facility && v.facility !== cat.facility) return false;
      return true;
    })
    .sort(compareByGuestRating)
    .slice(0, 8);
}

function getMostBookedVenues(venues: Venue[]) {
  return [...venues]
    .filter((venue) => venue.bookingCount > 0 || venue.reviews > 0)
    .sort((a, b) => {
      if (b.bookingCount !== a.bookingCount) return b.bookingCount - a.bookingCount;
      if (b.reviews !== a.reviews) return b.reviews - a.reviews;
      return b.rating - a.rating;
    })
    .slice(0, 6);
}

function DeferredSection({
  children,
  fallback,
  className,
}: {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  className?: string;
}) {
  const [shouldRender, setShouldRender] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      { rootMargin: "240px 0px" },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={className}>
      {shouldRender ? children : fallback}
    </div>
  );
}

/** Tracks whether a horizontally-scrollable row has more content hidden past
 *  either edge, so we can show a fade-out cue only when it's actually needed. */
function useEdgeFade<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const update = () => {
      setAtStart(el.scrollLeft <= 4);
      setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
    };

    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  return { ref, showLeftFade: !atStart, showRightFade: !atEnd };
}

function HomePage() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [allVenues, setAllVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const categoryFade = useEdgeFade<HTMLDivElement>();
  const trendingFade = useEdgeFade<HTMLDivElement>();

  useEffect(() => {
    let isMounted = true;

    async function loadVenues() {
      setLoading(true);
      setError(null);

      try {
        const venues = await VenueService.getVenues();
        if (!isMounted) return;
        setAllVenues(venues);
      } catch (err) {
        if (!isMounted) return;
        setAllVenues([]);
        setError(
          err instanceof Error
            ? err.message
            : "We couldn't load venues right now. Please try again in a moment.",
        );
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    void loadVenues();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredVenues = useMemo(
    () => getFilteredVenues(allVenues, activeCategory),
    [activeCategory, allVenues],
  );

  const mostBooked = useMemo(() => getMostBookedVenues(allVenues), [allVenues]);

  return (
    <>
      {/* Hero */}
      <section className="relative isolate -mt-[100px] flex min-h-[85vh] lg:min-h-[100vh] items-center overflow-hidden bg-neutral-900 pt-[100px]">
        <img
          src={heroImg}
          alt="Luxury courtyard resort in Oyo State at golden hour"
          className="absolute inset-0 -z-10 size-full object-cover"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-hero" />
        <div className="mx-auto w-full max-w-7xl px-4 pb-12 pt-12 sm:px-6 sm:pb-16 sm:pt-16 lg:px-8 lg:pb-24 lg:pt-24">
          <h1 className="text-balance max-w-2xl lg:max-w-3xl font-display text-3xl font-bold leading-[1.1] text-white sm:text-4xl md:text-5xl lg:text-6xl animate-fade-up">
            Find the ideal space for vacations or events.
          </h1>
          <p className="text-balance mt-4 sm:mt-6 max-w-xl text-sm text-white/85 sm:text-base animate-fade-up delay-75">
            Search through our diverse vacation rental and venue listings to find the perfect place for an unforgettable getaway, gathering or stay.
          </p>
          <div className="mt-8 sm:mt-10 animate-fade-up delay-150">
            <SearchBar />
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="bg-muted/40 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Why book from us?</p>
            <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl">A reservation platform built for Oyo.</h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {[
              { icon: ShieldCheck, title: "Verified venues", body: "Every listing is hand-checked for quality, safety and accuracy." },
              { icon: CalendarCheck, title: "Flexible booking", body: "Reserve in minutes, reschedule on your terms, never lose a deposit." },
              { icon: Rocket, title: "Premium support", body: "Local concierge support from arrival to checkout, anywhere in the state." },
            ].map((f) => (
              <div key={f.title} className="group flex flex-col items-center rounded-2xl border border-border/60 bg-card p-7 text-center transition-all hover:-translate-y-1 hover:shadow-elegant">
                <div className="grid size-12 place-items-center rounded-xl bg-primary text-primary-foreground transition-transform group-hover:scale-110">
                  <f.icon className="size-5" />
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold">{f.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Browse by category */}
      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between gap-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Browse</p>
              <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl">Explore by category</h2>
            </div>
            <Link to="/hotels" className="hidden text-sm font-semibold text-primary hover:underline sm:inline">
              View all →
            </Link>
          </div>

          {error && (
            <div className="mt-6 overflow-hidden rounded-2xl border border-amber-200/70 bg-amber-50/80 shadow-soft">
              <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex gap-4">
                  <div className="grid size-11 shrink-0 place-items-center rounded-xl bg-amber-100 text-amber-700">
                    <WifiOff className="size-5" />
                  </div>
                  <div>
                    <h3 className="font-display text-base font-semibold text-amber-950">
                      We couldn't load the venue listings.
                    </h3>
                    <p className="mt-1 max-w-2xl text-sm leading-6 text-amber-900/80">
                      {error || "Please check your internet connection and try again."}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.location.reload()}
                  className="shrink-0 gap-2 border-amber-300 bg-white text-amber-950 hover:bg-amber-100 hover:text-amber-950"
                >
                  <RefreshCcw className="size-3.5" />
                  Try again
                </Button>
              </div>
            </div>
          )}

          {/* Category tabs */}
          <div className="relative mt-8">
            <div
              ref={categoryFade.ref}
              className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8"
            >
              {BROWSABLE_CATEGORIES.map((cat) => {
                const Icon = ICON_MAP[cat.icon] || LayoutGrid;
                const isActive = activeCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`flex shrink-0 items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all ${isActive
                        ? "bg-primary text-primary-foreground shadow-elegant"
                        : "border border-border bg-card text-muted-foreground hover:border-primary/30 hover:text-foreground"
                      }`}
                  >
                    <Icon className="size-4" />
                    {cat.label}
                  </button>
                );
              })}
            </div>
            {/* Edge fades — only shown when there's more to scroll to on that side */}
            <div
              aria-hidden
              className={`pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-background to-transparent transition-opacity duration-200 sm:w-14 ${
                categoryFade.showLeftFade ? "opacity-100" : "opacity-0"
              }`}
            />
            <div
              aria-hidden
              className={`pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-background to-transparent transition-opacity duration-200 sm:w-14 ${
                categoryFade.showRightFade ? "opacity-100" : "opacity-0"
              }`}
            />
          </div>

          <DeferredSection
            className="mt-8"
            fallback={
              <div className="grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <VenueCardSkeleton key={i} />
                ))}
              </div>
            }
          >
            <div className="grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
              {loading
                ? Array.from({ length: 8 }).map((_, i) => <VenueCardSkeleton key={i} />)
                : !error && filteredVenues.length > 0
                  ? filteredVenues.map((v) => <VenueCard key={v.id} venue={v} />)
                  : null}
            </div>
          </DeferredSection>

          {!loading && !error && filteredVenues.length === 0 && (
            <div className="py-16 text-center">
              <p className="text-muted-foreground">No venues found in this category yet.</p>
              <Button variant="outline" className="mt-4 rounded-full" onClick={() => setActiveCategory("all")}>Show all</Button>
            </div>
          )}
        </div>
      </section>

      {/* Most booked — a horizontal "trending" rail, deliberately distinct from
          the category grid above so the two sections don't read as duplicates. */}
      <section className="bg-gradient-warm py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between gap-6">
            <div>
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                <Flame className="size-3.5" /> Trending now
              </p>
              <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl">Guest favorites with real demand.</h2>
            </div>
            <Link to="/hotels" className="hidden text-sm font-semibold text-primary hover:underline sm:inline">
              View all →
            </Link>
          </div>
          <DeferredSection
            className="relative mt-10"
            fallback={
              <div className="flex gap-5 overflow-x-auto pt-4 pb-2 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="w-[260px] shrink-0 sm:w-[300px]">
                    <VenueCardSkeleton />
                  </div>
                ))}
              </div>
            }
          >
            <div className="relative">
              <div
                ref={trendingFade.ref}
                className="flex snap-x snap-mandatory gap-5 overflow-x-auto pt-4 pb-2 scrollbar-hide -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8"
              >
                {loading
                  ? Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="w-[260px] shrink-0 sm:w-[300px]">
                        <VenueCardSkeleton />
                      </div>
                    ))
                  : !error && mostBooked.length > 0
                    ? mostBooked.map((v, i) => (
                        <div key={v.id} className="relative w-[260px] shrink-0 snap-start sm:w-[300px]">
                          {i < 3 && (
                            <span className="absolute -top-3 left-3 z-10 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-gradient-to-r from-amber-500 to-orange-500 px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow-lg ring-2 ring-background">
                              <Flame className="size-2.5 shrink-0" /> #{i + 1} Trending
                            </span>
                          )}
                          <VenueCard venue={v} />
                        </div>
                      ))
                    : (
                      <div className="w-full rounded-3xl bg-card p-10 text-center text-muted-foreground">
                        {error
                          ? "We couldn't load featured venues right now. Please try again shortly."
                          : "No booking activity to feature yet."}
                      </div>
                    )}
              </div>
              {!loading && !error && mostBooked.length > 0 && (
                <>
                  <div
                    aria-hidden
                    className={`pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-[oklch(0.98_0.008_72)] to-transparent transition-opacity duration-200 sm:w-16 dark:from-[oklch(0.18_0.02_25)] ${
                      trendingFade.showLeftFade ? "opacity-100" : "opacity-0"
                    }`}
                  />
                  <div
                    aria-hidden
                    className={`pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-[oklch(0.98_0.008_72)] to-transparent transition-opacity duration-200 sm:w-16 dark:from-[oklch(0.18_0.02_25)] ${
                      trendingFade.showRightFade ? "opacity-100" : "opacity-0"
                    }`}
                  />
                </>
              )}
            </div>
          </DeferredSection>
        </div>
      </section>

      {/* Essentials */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Essentials</p>
          <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl">Everything you need for a perfect stay.</h2>
        </div>
        <div className="mt-14 grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { icon: CalendarCheck, title: "Effortless reservations", body: "Live availability, instant confirmation and a clean receipt every time." },
            { icon: BedDouble, title: "Comfort first", body: "From boutique suites to grand ballrooms, every venue is curated for comfort." },
            { icon: Headphones, title: "24/7 concierge", body: "Talk to a real human in Ibadan whenever you need a hand." },
            { icon: ShieldCheck, title: "Safe & secure", body: "Encrypted payments, verified hosts and trusted reviews." },
            { icon: Star, title: "Quality guaranteed", body: "Stay confident — if it isn't right, we'll make it right." },
            { icon: MapPin, title: "Across Oyo State", body: "Ibadan to Igboho, Saki to Ogbomoso — coverage statewide." },
          ].map((e) => (
            <div key={e.title} className="flex gap-4">
              <div className="grid size-11 shrink-0 place-items-center rounded-xl bg-accent text-primary">
                <e.icon className="size-5" />
              </div>
              <div>
                <h3 className="font-display text-lg font-semibold">{e.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{e.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA — guest booking CTA and a separate host lead-gen CTA, kept visually
          distinct so each audience gets a message that's actually for them. */}
      <section className="mx-auto max-w-7xl space-y-5 px-4 pb-24 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-3xl bg-primary p-8 text-center text-primary-foreground shadow-elegant sm:p-10 md:p-14 md:text-left">
          <div className="grid items-center gap-8 md:grid-cols-[1fr_auto]">
            <div>
              <h2 className="font-display text-3xl font-bold sm:text-4xl">Plan your next stay or event.</h2>
              <p className="mt-3 max-w-xl text-primary-foreground/80 md:mx-0 mx-auto">
                Browse Oyo's finest venues and reserve your perfect space in minutes.
              </p>
            </div>
            <div className="flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
              <Link to="/hotels" className="w-full sm:w-auto"><Button size="lg" variant="secondary" className="w-full sm:w-auto">Browse hotels</Button></Link>
              <Link to="/event-centres" className="w-full sm:w-auto"><Button size="lg" variant="outline" className="w-full sm:w-auto border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white">Event centres</Button></Link>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center gap-4 rounded-3xl border border-dashed border-primary/30 bg-primary/5 p-6 text-center sm:flex-row sm:justify-between sm:text-left">
          <div>
            <h3 className="font-display text-lg font-semibold sm:text-xl">Own a venue in Oyo State?</h3>
            <p className="mt-1 max-w-md text-sm text-muted-foreground">
              List your hotel, event centre or open space and start earning from guest bookings today.
            </p>
          </div>
          <Link to="/business-type" className="w-full shrink-0 sm:w-auto">
            <Button size="lg" variant="outline" className="w-full rounded-full border-primary/40 text-primary hover:bg-primary/10 sm:w-auto">
              List your property
            </Button>
          </Link>
        </div>
      </section>
    </>
  );
}



